<template>
  <v-container fluid grid-list-xl>
    <div>
      <v-layout row wrap justify-end pr-1 class="mb-2">
        <v-btn color="primary" class="mb-2" @click="searchModal = true">
          <v-icon left>fa-search</v-icon>
          Search
        </v-btn>
      </v-layout>

      <v-data-table
        :headers="columnsDisplayed"
        :items="rows"
        :pagination.sync="pagination"
        :loading="loading"
        :total-items="totalRows"
        :rows-per-page-items="pageCountOptions"
        item-key="id"
        class="elevation-1"
        must-sort
        no-data-text="No data found"
        no-results-text="No results found"
        @update:pagination="search"
      >
        <template slot="headers" slot-scope="props">
          <tr>
            <th
              v-for="(header) in props.headers"
              :key="header.value"
              v-show="header.visible"
              :class="['font-weight-bold text-xs-left column sortable pa-1 ma-0', pagination.descending ? 'desc' : 'asc', header.value === pagination.sortBy ? 'active' : '']"
              @click="changeSort(header.value)"
            >
              {{ header.text }}
              <v-icon small>arrow_upward</v-icon>
            </th>
            <th class="ma-0 pa-0 text-xs-right">
              <v-btn icon flat color="primary" @click="reset">
                <v-icon>fa-refresh</v-icon>
              </v-btn>
            </th>
          </tr>
        </template>

        <template slot="items" slot-scope="props">
          <tr>
            <td
              v-for="(header) in columnsDisplayed"
              :key="header.value"
              v-show="header.visible"
              class="pa-1 ma-0"
            >
              <template v-if="header.value === 'id'">
                <a class="primary--text font-weight-bold text-no-wrap" title="View details" @click="openDetail(props.item.id)">
                  <v-icon small color="primary" class="mr-1">fa-search-plus</v-icon>{{ props.item.id }}
                </a>
              </template>

              <template v-else-if="header.type === 'datetime'">
                {{ datetimeFromUtc(props.item[header.value]) }}
              </template>

              <template v-else>
                {{ props.item[header.value] }}
              </template>
            </td>
            <td class="ma-0 pa-0 text-xs-right"></td>
          </tr>
        </template>

        <template slot="no-data">
          No results found
        </template>
      </v-data-table>
    </div>

    <!-- Search -->
    <v-dialog v-model="searchModal" max-width="800px" @submit.prevent @keydown.esc.prevent="searchModal=false">
      <v-toolbar dense>
        <v-toolbar-title>Search</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon flat color="primary" @click.native="searchModal = false">
          <v-icon>fa-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card>
        <v-card-text>
          <v-form @keyup.native.enter="searchNew">
            <v-container fluid>
              <v-layout row wrap>
                <v-flex xs12 sm4>
                  <v-text-field label="Tracking ID" v-model="filter.tracking_id" box hide-details />
                </v-flex>
                <v-flex xs6 sm4>
                  <v-text-field label="Method" v-model="filter.method" box hide-details />
                </v-flex>
                <v-flex xs6 sm4>
                  <v-text-field label="API" v-model="filter.api" box hide-details />
                </v-flex>
                <v-flex xs12 sm4>
                  <v-text-field label="Server Request ID" v-model="filter.server_request_id" box hide-details />
                </v-flex>
                <v-flex xs6 sm4>
                  <v-text-field label="IP Address" v-model="filter.ip_address" box hide-details />
                </v-flex>
                <v-flex xs6 sm4>
                  <v-text-field label="Fingerprint" v-model="filter.fingerprint" box hide-details />
                </v-flex>

                <v-flex xs12 sm6>
                  <v-text-field label="Created From" type="datetime-local" v-model="filter.created_start_date" box hide-details />
                </v-flex>
                <v-flex xs12 sm6>
                  <v-text-field label="Created To" type="datetime-local" v-model="filter.created_end_date" box hide-details />
                </v-flex>
              </v-layout>

              <v-divider class="primary" />

              <v-layout row wrap justify-end>
                <v-btn color="primary" @click="searchNew">Search</v-btn>
                <v-btn @click="reset">Clear</v-btn>
              </v-layout>
            </v-container>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Detail -->
    <v-dialog v-model="detailModal" scrollable max-width="900px" @keydown.esc.prevent="detailModal=false">
      <v-card>
        <v-toolbar dense card color="primary" dark>
          <v-toolbar-title>API Request Detail{{ detail ? ' #' + detail.id : '' }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon flat @click.native="detailModal = false">
            <v-icon>fa-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text>
          <div v-if="detailLoading" class="text-xs-center pa-4">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>

          <div v-else-if="detail">
            <!-- Overview -->
            <v-layout row wrap>
              <v-flex xs12 sm6 md4><strong>ID:</strong> {{ detail.id }}</v-flex>
              <v-flex xs12 sm6 md4><strong>Created:</strong> {{ datetimeFromUtc(detail.created) }}</v-flex>
              <v-flex xs12 sm6 md4><strong>Method:</strong> {{ detail.method }}</v-flex>
              <v-flex xs12 sm6 md4><strong>API:</strong> {{ detail.api }}</v-flex>
              <v-flex xs12 sm6 md4><strong>IP Address:</strong> {{ detail.ip_address }}</v-flex>
              <v-flex xs12 sm6 md4><strong>Fingerprint:</strong> {{ detail.fingerprint || '—' }}</v-flex>
              <v-flex xs12 sm6><strong>Tracking ID:</strong> {{ detail.tracking_id || '—' }}</v-flex>
              <v-flex xs12 sm6><strong>Server Request ID:</strong> {{ detail.server_request_id || '—' }}</v-flex>
            </v-layout>

            <v-divider class="my-3" />

            <!-- Request / Response as collapsible panels (default closed) -->
            <v-expansion-panel expand class="mt-2">
              <v-expansion-panel-content>
                <template v-slot:header>
                  <div class="subheading font-weight-bold">Request</div>
                </template>
                <v-card>
                  <v-card-text>
                    <pre class="detail-pre">{{ formatRaw(detail.raw_request) || '—' }}</pre>
                  </v-card-text>
                </v-card>
              </v-expansion-panel-content>

              <v-expansion-panel-content>
                <template v-slot:header>
                  <div class="subheading font-weight-bold">Request Headers</div>
                </template>
                <v-card>
                  <v-card-text>
                    <pre class="detail-pre">{{ formatRaw(detail.raw_request_headers) || '—' }}</pre>
                  </v-card-text>
                </v-card>
              </v-expansion-panel-content>

              <v-expansion-panel-content>
                <template v-slot:header>
                  <div class="subheading font-weight-bold">Response</div>
                </template>
                <v-card>
                  <v-card-text>
                    <pre class="detail-pre">{{ formatRaw(detail.raw_response) || '—' }}</pre>
                  </v-card-text>
                </v-card>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Moment from 'moment'

import Bus from '@/bus'
import ApiMixin from '@/mixins/api'
import AuthMixin from '@/mixins/auth'
import DisplayMixin from '@/mixins/display'
import StoreMixin from '@/store/mixin'

export default {
  mixins: [ApiMixin, AuthMixin, DisplayMixin, StoreMixin],

  data: function () {
    return {
      columns: [
        { value: 'id', text: 'ID', sortable: true, visible: true },
        { value: 'tracking_id', text: 'Tracking ID', sortable: true, visible: true },
        { value: 'method', text: 'Method', sortable: true, visible: true },
        { value: 'api', text: 'API', sortable: true, visible: true },
        { value: 'server_request_id', text: 'Server Request ID', sortable: true, visible: true },
        { value: 'ip_address', text: 'IP Address', sortable: true, visible: true },
        { value: 'created', text: 'Created', sortable: true, visible: true, type: 'datetime' }
      ],
      pagination: this.defaultPagination(),
      rows: [],
      totalRows: 0,
      pageCountOptions: [5, 10, 25, 50, 100],
      loading: true,

      searchModal: false,

      detailModal: false,
      detailLoading: false,
      detail: null,

      filter: this.defaultFilter()
    }
  },

  computed: {
    columnsDisplayed: function () {
      return this.columns.filter(column => column.visible)
    }
  },

  methods: {
    defaultFilter () {
      return {
        tracking_id: '',
        method: '',
        api: '',
        server_request_id: '',
        ip_address: '',
        fingerprint: '',
        // Default to the past hour.
        created_start_date: Moment().subtract(1, 'hour').format('YYYY-MM-DDTHH:mm'),
        created_end_date: ''
      }
    },

    defaultPagination () {
      return {
        sortBy: 'created',
        descending: true,
        rowsPerPage: this.$store.state.paginationDefaults.rowsPerPage,
        page: 1
      }
    },

    datetimeFromUtc (date) {
      if (date === null || !date) {
        return ''
      }
      return Moment.utc(date).local().format('MMM-DD-YYYY hh:mm:ssA')
    },

    // raw_request/raw_request_headers/raw_response are stored as text; render
    // JSON readably, otherwise show the raw string (e.g. XML).
    formatRaw (value) {
      if (value === null || value === undefined || value === '') {
        return ''
      }
      if (typeof value === 'object') {
        return Object.keys(value).length ? JSON.stringify(value, null, 2) : ''
      }
      if (typeof value === 'string') {
        let trimmed = value.trim()
        if (trimmed === '' || trimmed === '{}' || trimmed === 'null') {
          return ''
        }
        try {
          return JSON.stringify(JSON.parse(value), null, 2)
        } catch (e) {
          return value
        }
      }
      return String(value)
    },

    searchNew () {
      this.pagination.page = 1
      this.search()
    },

    async search () {
      this.loading = true

      try {
        let filter = { ...this.filter }

        if (filter.created_start_date) {
          filter.created_start_date = Moment(filter.created_start_date).utc().format('YYYY-MM-DD HH:mm:ss')
        }
        if (filter.created_end_date) {
          filter.created_end_date = Moment(filter.created_end_date).utc().format('YYYY-MM-DD HH:mm:ss')
        }

        let result = await this.api({
          method: 'get',
          url: '/api_requests',
          params: {
            filter: filter,
            page: this.pagination.page - 1,
            perPage: this.pagination.rowsPerPage,
            orderBy: this.pagination.sortBy,
            orderDirection: this.pagination.descending ? 'desc' : 'asc'
          }
        })
        this.rows = result.data.rows
        this.totalRows = result.data.count
        this.searchModal = false
      } catch (err) {
        Bus.$emit('openModal', {
          status: 'error',
          message: err
        })
      } finally {
        this.loading = false
      }
    },

    async openDetail (id) {
      this.detail = null
      this.detailLoading = true
      this.detailModal = true

      try {
        let result = await this.api({
          method: 'get',
          url: `/api_requests/${id}`
        })
        this.detail = result.data.row
      } catch (err) {
        this.detailModal = false
        Bus.$emit('openModal', {
          status: 'error',
          message: err
        })
      } finally {
        this.detailLoading = false
      }
    },

    reset () {
      this.filter = this.defaultFilter()
      this.pagination = this.defaultPagination()

      this.search()
    },

    changeSort (column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending
      } else {
        this.pagination.sortBy = column
        this.pagination.descending = false
      }
      this.search()
    }
  }
}
</script>

<style scoped>
.detail-pre {
  white-space: pre-wrap;
  word-break: break-word;
  overflow-x: auto;
  max-height: 300px;
  background: rgba(0, 0, 0, 0.04);
  padding: 8px;
  border-radius: 4px;
  font-size: 12px;
}
a {
  cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// src/components/manage/ApiRequests.vue